import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Comprehensive date and time formatting composable
 * Combines all date/time formatting needs in one place with i18n support
 */
export function useDateFormatting() {
  const { t, locale, d } = useI18n()
  
  // Basic formatting functions
  const formatDate = (date: Date | null): string => {
    if (!date) return ''
    return d(date, 'long')
  }
  
  const formatShortDate = (date: Date | null): string => {
    if (!date) return ''
    return d(date, 'short')
  }
  
  const formatDateWithoutYear = (date: Date | null): string => {
    if (!date) return ''
    return d(date, 'shortWithoutYear')
  }
  
  const formatBookingDate = (date: Date | null): string => {
    if (!date) return ''
    return d(date, 'booking')
  }
  
  const getShortWeekday = (date: Date | null): string => {
    if (!date) return ''
    return d(date, 'weekday')
  }
  
  const formatTime = (timeString: string | null): string => {
    if (!timeString) return ''
    
    const [hours, minutes] = timeString.split(':').map(Number)
    
    // Create a date object with the time
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    
    return d(date, 'time')
  }
  
  // Advanced formatting with relative time
  const formatDay = (date: Date | null): string => {
    if (!date) return ''
    
    // Get today's date for comparison
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Create dates for tomorrow and one week from today
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    
    const oneWeekFromToday = new Date(today)
    oneWeekFromToday.setDate(oneWeekFromToday.getDate() + 7)
    
    // Normalize the input date for comparison
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    // Check if the date is today or tomorrow
    if (compareDate.getTime() === today.getTime()) {
      return t('dateTime.today')
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return t('dateTime.tomorrow')
    }
    
    // Get the weekday for this date
    const weekdayIndex = date.getDay()
    const weekday = t(`dateTime.days.${weekdayIndex}`)
    
    // If date is within a week, just return the weekday
    if (compareDate < oneWeekFromToday) {
      return weekday
    }
    
    // For dates beyond a week, return weekday with short date format
    const shortDate = d(date, 'shortWithoutYear')
    
    return t('dateTime.weekdayWithDate', { 
      weekday, 
      date: shortDate 
    }, `${weekday}, ${shortDate}`)
  }
  
  // Utility functions for date comparison
  const isPastDate = (date: Date | null): boolean => {
    if (!date) return false
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    return compareDate < today
  }
  
  const isSameDate = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false
    
    const d1 = new Date(date1)
    const d2 = new Date(date2)
    
    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    )
  }
  
  // Get day number as a string
  const getDayNumber = (date: Date | null): string => {
    if (!date) return ''
    return String(date.getDate())
  }
  
  // Booking-specific formatting
  const createBookingMessage = (date: Date | null, time: string | null): string => {
    if (!date || !time) {
      return t('confirmation.checkAppDetails')
    }
    
    const formattedTimeStr = formatTime(time)
    
    // Get today and tomorrow dates for comparison
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    
    // Compare the date
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    // Use different translation keys based on the date type
    if (compareDate.getTime() === today.getTime()) {
      return t('confirmation.seeYouTodayAtTime', { 
        time: formattedTimeStr
      }, `See you today at ${formattedTimeStr}`)
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return t('confirmation.seeYouTomorrowAtTime', { 
        time: formattedTimeStr
      }, `See you tomorrow at ${formattedTimeStr}`)
    } else {
      const formattedDayStr = formatDay(date)
      return t('confirmation.seeYouOnDayAtTime', { 
        time: formattedTimeStr, 
        day: formattedDayStr 
      }, `See you on ${formattedDayStr} at ${formattedTimeStr}`)
    }
  }
  
  return {
    // Basic formatting
    formatDate,
    formatShortDate,
    formatDateWithoutYear,
    formatBookingDate,
    getShortWeekday,
    formatTime,
    
    // Advanced formatting
    formatDay,
    
    // Utility functions
    isPastDate,
    isSameDate,
    getDayNumber,
    
    // Booking-specific
    createBookingMessage
  }
}

/**
 * Standalone date utility functions for use outside of components
 * These don't depend on Vue's composition API or i18n
 */
export const dateUtils = {
  getDayNumber: (date: Date): string => String(date.getDate()),
  
  formatTimeStandalone: (timeString: string): string => {
    if (!timeString) return ''
    
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours, 10)
    
    const date = new Date()
    date.setHours(hour)
    date.setMinutes(parseInt(minutes, 10))
    
    return date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  },
  
  formatDateStandalone: (date: Date): string => {
    if (!date) return ''
    
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    
    return date.toLocaleDateString(undefined, options)
  },
  
  formatShortDateStandalone: (date: Date): string => {
    if (!date) return ''
    
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    }
    
    return date.toLocaleDateString(undefined, options)
  },
  
  formatBookingDateStandalone: (date: Date): string => {
    if (!date) return ''
    
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }
    
    return date.toLocaleDateString(undefined, options)
  },
  
  getShortWeekdayStandalone: (date: Date): string => {
    if (!date) return ''
    
    const options: Intl.DateTimeFormatOptions = { weekday: 'short' }
    return date.toLocaleDateString(undefined, options).split(',')[0]
  },
  
  isPastDate: (date: Date): boolean => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }
} 